/**
 * =============================================================================
 * IDS CRM Web - Engineer bulletins: one bulletin
 * =============================================================================
 *
 * Reading, and the whole draft-to-published journey (Gareth 2026-09-11..13):
 *
 *  - A draft is edited or discarded by its author or a reviewer.
 *  - An author who is not a reviewer sends it for review; reviewers are
 *    messaged once ("manager draft notification - yes").
 *  - A reviewer publishes. Before publishing they are ASKED about each
 *    published bulletin sharing a fault code, unticked, and only what they
 *    tick is replaced ("Yes, dont replace" — nothing replaced automatically).
 *  - The groups on a bulletin are who gets TOLD, not who may read. With no
 *    groups it can still be published, and the dialog says nobody is told.
 *  - A published bulletin is never edited; a newer one replaces it.
 *
 * The text is plain text shown with white-space: pre-wrap. Never render it as
 * HTML or markdown: anyone signed in can write one.
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { listReturn, RESTORE_LIST_STATE } from '../utils/listReturn';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { BulletinStatusBadge, FaultCodeChips } from '../components/BulletinParts';
import { authedFileHref } from '../utils/files';
import { formatDate } from '../utils/format';
import { parseFaultCodes } from '../utils/faultCodes';
import {
  canEditBulletin,
  isBulletinAuthor,
  isBulletinReviewer,
  formatFileSize,
  publishNotice,
  submitNotice
} from '../utils/bulletins';
import './Bulletins.css';

export function PublishDialog({ bulletin, authFetch, user, onCancel, onPublished }) {
  const codesKey = parseFaultCodes(bulletin.fault_codes).join(',');
  const groups = Array.isArray(bulletin.groups) ? bulletin.groups : [];
  // When someone else publishes it, the API also tells the author their
  // bulletin is out. Say so, or "nobody will be notified" would be untrue.
  const authorTold = !isBulletinAuthor(user, bulletin);
  const authorName = bulletin.created_by_name || 'the person who wrote it';

  const [candidates, setCandidates] = useState([]);
  const [checking, setChecking] = useState(Boolean(codesKey));
  const [checkError, setCheckError] = useState('');
  // Bumped by "Try again" to run the same-code check once more.
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [ticked, setTicked] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);

  // Focus moves into the dialog as it opens, so a screen reader announces it
  // and Tab reaches the replace questions, not the Edit and Discard buttons
  // behind the overlay.
  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
  }, []);

  // Escape closes it (not mid-publish), and Tab stays inside it.
  useEffect(() => {
    const onKeyDown = (event) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (event.key === 'Escape') {
        if (!publishing) {
          event.preventDefault();
          onCancel();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled])'));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, publishing]);

  useEffect(() => {
    if (!codesKey) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setChecking(true);
      setCheckError('');
      try {
        const params = new URLSearchParams({ status: 'published', fault_codes: codesKey, limit: '50' });
        const response = await authFetch(`/api/v1/bulletins?${params}`);
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setCheckError(body.message || 'Could not check for older bulletins with these fault codes.');
          return;
        }
        const rows = Array.isArray(body.data) ? body.data : [];
        setCandidates(rows.filter(row => row.bulletin_id !== bulletin.bulletin_id));
      } catch (err) {
        if (!cancelled) setCheckError('Could not check for older bulletins with these fault codes.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, codesKey, bulletin.bulletin_id, checkAttempt]);

  const toggle = (bulletinId) => {
    setTicked(prev => (prev.includes(bulletinId)
      ? prev.filter(entry => entry !== bulletinId)
      : [...prev, bulletinId]));
  };

  const publish = async () => {
    setPublishing(true);
    setError('');
    try {
      // Only what was ticked, in the order it was listed.
      const replaces = candidates.map(row => row.bulletin_id).filter(entry => ticked.includes(entry));
      const response = await authFetch(`/api/v1/bulletins/${bulletin.bulletin_id}/publish`, {
        method: 'POST',
        body: JSON.stringify({ replaces })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.message || 'Could not publish this bulletin. Try again.');
        setPublishing(false);
        return;
      }
      onPublished(body);
    } catch (err) {
      setError('Could not publish this bulletin. Check your connection and try again.');
      setPublishing(false);
    }
  };

  return (
    <div className="modal-overlay bulletin-dialog-overlay" onClick={publishing ? undefined : onCancel}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="modal-content bulletin-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulletin-publish-heading"
        onClick={e => e.stopPropagation()}
      >
        <h2 id="bulletin-publish-heading">Publish this bulletin?</h2>

        <section className="bulletin-dialog-section">
          <h3>Does it replace an older bulletin?</h3>
          {!codesKey ? (
            <p className="bulletin-hint">
              This bulletin has no fault codes, so there are no older bulletins to check it against.
            </p>
          ) : checking ? (
            <p className="bulletin-hint">Checking for published bulletins with the same fault codes…</p>
          ) : checkError ? (
            <>
              <p className="bulletin-error" role="alert">{checkError}</p>
              {/* Publishing waits for the check: a publish without it would
                  never ask "Does this replace ...?", and a published bulletin
                  can't be changed afterwards (Gareth 2026-09-13: the publisher
                  is asked, nothing is replaced automatically). */}
              <p className="bulletin-hint">
                It can't be published until this check works, so you can say whether it replaces an older bulletin.
              </p>
              <div className="bulletin-actions">
                <button
                  type="button"
                  className="btn-secondary bulletin-button"
                  onClick={() => {
                    setCheckAttempt(attempt => attempt + 1);
                    // The button goes while checking; keep focus in the dialog.
                    if (dialogRef.current) dialogRef.current.focus();
                  }}
                >
                  Try again
                </button>
              </div>
            </>
          ) : candidates.length === 0 ? (
            <p className="bulletin-hint">No published bulletins share these fault codes.</p>
          ) : (
            <>
              <p className="bulletin-hint">
                These published bulletins share a fault code with this one. Tick any it replaces.
                Nothing is replaced unless you tick it.
              </p>
              <ul className="bulletin-choice-list">
                {candidates.map(row => (
                  <li key={row.bulletin_id}>
                    <label className="bulletin-choice">
                      <input
                        type="checkbox"
                        checked={ticked.includes(row.bulletin_id)}
                        onChange={() => toggle(row.bulletin_id)}
                      />
                      <span className="bulletin-choice-text">
                        <span className="bulletin-choice-name">Does this replace '{row.title}'?</span>
                        <FaultCodeChips codes={row.fault_codes} />
                        {row.published_at && (
                          <span className="bulletin-choice-meta">Published {formatDate(row.published_at)}</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="bulletin-dialog-section">
          <h3>Who will be told</h3>
          {groups.length > 0 ? (
            <p className="bulletin-hint">
              Members of {groups.map(group => group.name).join(', ')} will be told it has been published
              {authorTold ? `, and ${authorName} will be told their bulletin is out.` : '.'}
            </p>
          ) : (
            <p className="bulletin-warning">
              This bulletin isn't shared with any group, so{' '}
              {authorTold ? `only ${authorName} will be told, because they wrote it` : 'nobody will be notified'}.
              It will still be published for anyone to browse. To tell people, edit the draft and tick a group first.
            </p>
          )}
        </section>

        {error && <div className="bulletin-error" role="alert">{error}</div>}

        <div className="modal-actions bulletin-actions">
          <button type="button" className="btn-secondary bulletin-button" onClick={onCancel} disabled={publishing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary bulletin-button"
            onClick={publish}
            disabled={checking || publishing || Boolean(checkError)}
          >
            {publishing ? 'Publishing…' : 'Publish now'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulletinDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();
  const { confirmElement, requestConfirm } = useConfirm();

  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  // Following the "replaced by" link reuses this page; an answer for the
  // bulletin just left must not overwrite the one now open.
  const latestRequest = useRef(0);
  // Where keyboard focus goes when the publish dialog closes.
  const publishButtonRef = useRef(null);
  const headingRef = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++latestRequest.current;
    if (!quiet) {
      // Start clean. Kept, the bulletin just left would be drawn again under
      // the new address if this one fails, and its error would never show.
      setLoading(true);
      setBulletin(null);
      setMissing(false);
      setLoadError('');
    }
    // A quiet reload follows publishing or sending for review; the bulletin
    // stays on screen, so say it may be behind rather than that it won't load.
    const staleMessage = 'Could not refresh this bulletin, so what is shown may be out of date. Reload the page to check.';
    try {
      const response = await authFetch(`/api/v1/bulletins/${id}`);
      const body = await response.json().catch(() => ({}));
      if (requestId !== latestRequest.current) return;
      if (response.status === 404) {
        setMissing(true);
        setBulletin(null);
        return;
      }
      if (!response.ok) {
        setLoadError(quiet ? staleMessage : (body.message || 'Could not load this bulletin. Try again.'));
        return;
      }
      setMissing(false);
      setLoadError('');
      setBulletin(body.data || null);
    } catch (err) {
      if (requestId === latestRequest.current) {
        setLoadError(quiet ? staleMessage : 'Could not load this bulletin. Check your connection and try again.');
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    setNotice('');
    setActionError('');
    setPublishOpen(false);
    load();
  }, [load]);

  const sendForReview = async () => {
    setBusy('submit');
    setActionError('');
    setNotice('');
    try {
      const response = await authFetch(`/api/v1/bulletins/${id}/submit`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setActionError(body.message || 'Could not send it for review. Try again.');
        return;
      }
      await load({ quiet: true });
      setNotice(submitNotice(body));
    } catch (err) {
      setActionError('Could not send it for review. Check your connection and try again.');
    } finally {
      setBusy('');
    }
  };

  const discard = () => {
    requestConfirm({
      title: 'Discard this draft?',
      message: `'${bulletin.title}' will be thrown away. This can't be undone.`,
      confirmLabel: 'Discard',
      danger: true,
      onConfirm: async () => {
        setBusy('discard');
        setActionError('');
        try {
          const response = await authFetch(`/api/v1/bulletins/${id}`, { method: 'DELETE' });
          const body = await response.json().catch(() => ({}));
          if (!response.ok) {
            setActionError(body.message || 'Could not discard the draft. Try again.');
            setBusy('');
            return;
          }
          // Replace, so Back does not return to a bulletin that is gone. The
          // list comes back as it was left (My drafts, say).
          navigate(listReturn(['/bulletins']).to, { replace: true, state: RESTORE_LIST_STATE });
        } catch (err) {
          setActionError('Could not discard the draft. Check your connection and try again.');
          setBusy('');
        }
      }
    });
  };

  const closePublish = () => {
    setPublishOpen(false);
    // Back to the button that opened it, so keyboard focus is not dropped.
    if (publishButtonRef.current) publishButtonRef.current.focus();
  };

  const handlePublished = async (result) => {
    setPublishOpen(false);
    await load({ quiet: true });
    setNotice(publishNotice(result));
    // The Publish button went with the draft; the heading is a stable place
    // for focus, instead of it falling to the page body.
    if (headingRef.current) headingRef.current.focus();
  };

  if (loading) {
    return <div className="bulletin-page"><div className="bulletin-loading">Loading…</div></div>;
  }

  if (missing) {
    return (
      <div className="bulletin-page">
        <div className="bulletin-panel">
          <h1 className="bulletin-panel-title">This bulletin doesn't exist or you can't see it</h1>
          <p className="bulletin-hint">
            It may have been a draft that was discarded. Drafts are only seen by the person who
            wrote them and by team leaders and managers.
          </p>
          <BackToList to="/bulletins" className="btn-secondary bulletin-button">Back to bulletins</BackToList>
        </div>
      </div>
    );
  }

  if (!bulletin) {
    return (
      <div className="bulletin-page">
        <BackToList to="/bulletins" className="bulletin-back">← Bulletins</BackToList>
        <div className="bulletin-error" role="alert">{loadError || 'Could not load this bulletin.'}</div>
      </div>
    );
  }

  const reviewer = isBulletinReviewer(user);
  const canEdit = canEditBulletin(user, bulletin);
  const isDraft = bulletin.status === 'draft';
  const canSubmit = isDraft && !bulletin.submitted_at && !reviewer && canEdit;
  const canPublish = isDraft && reviewer;
  const groups = Array.isArray(bulletin.groups) ? bulletin.groups : [];
  const attachments = Array.isArray(bulletin.attachments) ? bulletin.attachments : [];
  const replaces = Array.isArray(bulletin.replaces) ? bulletin.replaces : [];

  return (
    <div className="bulletin-page">
      {confirmElement}
      <BackToList to="/bulletins" className="bulletin-back">← Bulletins</BackToList>

      {bulletin.status === 'superseded' && (
        <div className="bulletin-banner">
          This bulletin has been replaced by a newer one.{' '}
          {bulletin.superseded_by_bulletin_id && (
            <Link to={`/bulletins/${bulletin.superseded_by_bulletin_id}`}>
              Read {bulletin.superseded_by_title ? `'${bulletin.superseded_by_title}'` : 'the newer bulletin'}
            </Link>
          )}
        </div>
      )}

      {notice && <div className="bulletin-notice" role="status">{notice}</div>}
      {actionError && <div className="bulletin-error" role="alert">{actionError}</div>}
      {loadError && <div className="bulletin-error" role="alert">{loadError}</div>}

      <article className="bulletin-detail">
        <div className="bulletin-detail-head">
          <h1 ref={headingRef} tabIndex={-1}>{bulletin.title}</h1>
          <BulletinStatusBadge bulletin={bulletin} />
        </div>

        <ul className="bulletin-detail-meta">
          <li>Written by {bulletin.created_by_name || 'unknown'} on {formatDate(bulletin.created_at)}</li>
          {bulletin.submitted_at && (
            <li>
              Sent for review on {formatDate(bulletin.submitted_at)}
              {bulletin.submitted_by_name ? ` by ${bulletin.submitted_by_name}` : ''}
            </li>
          )}
          {bulletin.published_at && (
            <li>
              Published{bulletin.published_by_name ? ` by ${bulletin.published_by_name}` : ''} on {formatDate(bulletin.published_at)}
            </li>
          )}
          {bulletin.superseded_at && <li>Replaced on {formatDate(bulletin.superseded_at)}</li>}
        </ul>

        {(canPublish || canSubmit || canEdit) && (
          <div className="bulletin-actions">
            {canPublish && (
              <button
                ref={publishButtonRef}
                type="button"
                className="btn-primary bulletin-button"
                onClick={() => setPublishOpen(true)}
                disabled={Boolean(busy)}
              >
                Publish
              </button>
            )}
            {canSubmit && (
              <button type="button" className="btn-primary bulletin-button" onClick={sendForReview} disabled={Boolean(busy)}>
                {busy === 'submit' ? 'Sending…' : 'Send for review'}
              </button>
            )}
            {canEdit && (
              <>
                <Link to={`/bulletins/${bulletin.bulletin_id}/edit`} className="btn-secondary bulletin-button">Edit</Link>
                <button type="button" className="btn-danger bulletin-button" onClick={discard} disabled={Boolean(busy)}>
                  {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
                </button>
              </>
            )}
          </div>
        )}

        {isDraft && bulletin.submitted_at && !reviewer && (
          <p className="bulletin-hint">
            Sent for review. A team leader or manager will check it and publish it.
          </p>
        )}

        <FaultCodeChips codes={bulletin.fault_codes} />

        <div className="bulletin-body">{bulletin.body}</div>

        {attachments.length > 0 && (
          <section className="bulletin-section">
            <h2>Files</h2>
            <ul className="bulletin-file-list">
              {attachments.map((file, index) => (
                <li key={file.attachment_id || index} className="bulletin-file">
                  <a href={authedFileHref(file.url)} target="_blank" rel="noreferrer">
                    {file.name || `File ${index + 1}`}
                  </a>
                  {formatFileSize(file.size) && <span className="bulletin-file-size">{formatFileSize(file.size)}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="bulletin-section">
          <p className="bulletin-shared">
            {groups.length > 0
              ? `Shared with: ${groups.map(group => group.name).join(', ')}`
              : 'Not shared with any group'}
          </p>
          {replaces.length > 0 && (
            <p className="bulletin-shared">
              Replaces:{' '}
              {replaces.map((entry, index) => (
                <React.Fragment key={entry.bulletin_id}>
                  {index > 0 && ', '}
                  <Link to={`/bulletins/${entry.bulletin_id}`}>{entry.title}</Link>
                </React.Fragment>
              ))}
            </p>
          )}
        </section>
      </article>

      {publishOpen && (
        <PublishDialog
          bulletin={bulletin}
          authFetch={authFetch}
          user={user}
          onCancel={closePublish}
          onPublished={handlePublished}
        />
      )}
    </div>
  );
}

export default BulletinDetail;
